import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  createMigrate,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import { authReducer } from './slices/auth.slice';
import { uiReducer } from './slices/ui.slice';
import { preferencesReducer } from './slices/preferences.slice';

/**
 * Custom storage adapter for redux-persist
 * Uses the Web Storage API directly with proper error handling
 */
const storage = {
  getItem: (key: string): Promise<string | null> => {
    return Promise.resolve(typeof window === 'undefined' ? null : window.localStorage.getItem(key));
  },
  setItem: (key: string, value: string): Promise<void> => {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, value);
    return Promise.resolve();
  },
  removeItem: (key: string): Promise<void> => {
    if (typeof window !== 'undefined') window.localStorage.removeItem(key);
    return Promise.resolve();
  },
};

/**
 * Auth persist configuration
 * Persist only durable auth data — `isLoading` and `error` are transient
 * request state and must NOT survive a refresh, otherwise the auth pages
 * rehydrate a stuck spinner / stale error banner.
 */
const authPersistConfig = {
  key: 'auth',
  version: 1,
  storage,
  blacklist: ['isLoading', 'error'],
};

/**
 * Preferences persist configuration
 * Display preferences (e.g. 12/24-hour time) are durable and per-browser, so
 * they get their own nested persistor — kept out of the transient `ui` slice.
 */
const preferencesPersistConfig = {
  key: 'preferences',
  version: 1,
  storage,
};

/**
 * Root reducer combining all slice reducers
 */
const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  ui: uiReducer,
  preferences: persistReducer(preferencesPersistConfig, preferencesReducer),
});

/**
 * Persist configuration
 *
 * `auth` is persisted by its own nested persistor (see authPersistConfig), so
 * it must NOT be whitelisted here — otherwise the root persistor would
 * re-persist the entire auth object (including the transient isLoading/error
 * that the nested config blacklists) and reintroduce the stuck-state bug.
 */
const persistConfig = {
  key: 'root',
  version: 2,
  storage,
  whitelist: [], // auth handled by nested persistor; ui is not persisted
  // v2 drops the legacy root-persisted auth blob, which stored the transient
  // isLoading/error and caused stuck loading/error state to survive refreshes.
  migrate: createMigrate({ 2: () => undefined }, { debug: false }),
};

/**
 * Persisted root reducer
 */
const persistedReducer = persistReducer(persistConfig, rootReducer);

/**
 * Create Redux store with middleware
 */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: import.meta.env.DEV,
});

/**
 * Create persistor for redux-persist
 */
export const persistor = persistStore(store);

/**
 * Root state type
 */
export type RootState = ReturnType<typeof store.getState>;

/**
 * App dispatch type
 */
export type AppDispatch = typeof store.dispatch;
